import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader2, TrendingUp, Activity } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { ChartExportButton } from '@/components/ui/chart-export-button';

interface ReadingTrendsChartProps {
  data: Array<{ date: string; count: number }>;
  loading?: boolean;
}

export function ReadingTrendsChart({ data = [], loading = false }: ReadingTrendsChartProps) {
  const chartData = useMemo(() => {
    // Filter out invalid dates and format the data
    return data
      .filter((item) => item && item.date && isValid(parseISO(item.date)))
      .map((item) => {
        try {
          const date = parseISO(item.date);
          return {
            date: item.date,
            formattedDate: isValid(date) ? format(date, 'MMM dd') : 'Invalid date',
            count: item.count,
          };
        } catch (error) {
          console.error('Error parsing date:', item.date, error);
          return null;
        }
      })
      .filter(Boolean) as {
      date: string;
      formattedDate: string;
      count: number;
    }[]; // Filter out any null entries
  }, [data]);

  // Calculate some useful statistics for chart context
  const totalReadings = chartData.reduce((sum, item) => sum + item.count, 0);
  const avgReadings = chartData.length > 0 ? Math.round(totalReadings / chartData.length) : 0;
  const maxReadings = chartData.length > 0 ? Math.max(...chartData.map((item) => item.count)) : 0;

  // Get date range for context
  const startDate = chartData.length > 0 ? format(parseISO(chartData[0].date), 'PPP') : '';
  const endDate = chartData.length > 0 ? format(parseISO(chartData[chartData.length - 1].date), 'PPP') : '';
  const dateRange = `${startDate} - ${endDate}`;

  // Enhanced custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length && chartData[label]) {
      try {
        const date = parseISO(chartData[label].date);
        const formattedDate = isValid(date) ? format(date, 'MMMM d, yyyy') : 'Unknown date';
        return (
          <div className="bg-background/95 backdrop-blur-md border border-border/50 rounded-lg shadow-lg p-4 min-w-[160px]">
            <p className="font-semibold text-sm mb-1">{formattedDate}</p>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <p className="text-sm">
                <span className="font-medium text-primary">{payload[0].value.toLocaleString()}</span>
                <span className="text-muted-foreground ml-1">readings</span>
              </p>
            </div>
          </div>
        );
      } catch (_error) {
        return (
          <div className="bg-background/95 backdrop-blur-md border border-border/50 rounded-lg shadow-lg p-3">
            <p className="text-sm text-muted-foreground">Error displaying data</p>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <Card className="col-span-1 border-border/50 shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <span>Reading Trends</span>
          </div>
          {!loading && chartData.length > 0 && (
            <span className="text-sm text-muted-foreground font-normal">{totalReadings.toLocaleString()} total</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        {loading ? (
          <div className="h-[280px] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[280px] flex flex-col items-center justify-center text-muted-foreground">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-3">
              <Activity className="h-8 w-8" />
            </div>
            <p className="text-sm font-medium">No reading history available</p>
            <p className="text-xs text-center mt-1">Sensor reading trends will appear here once data is collected</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Chart Container */}
            <div className="h-[200px] sm:h-[220px]" id="readings-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chartData}
                  margin={{
                    top: 10,
                    right: 10,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.1} />
                  <XAxis
                    dataKey="formattedDate"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: 'currentColor' }}
                    interval="preserveStartEnd"
                    className="text-muted-foreground"
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: 'currentColor' }}
                    tickFormatter={(value) => value.toLocaleString()}
                    className="text-muted-foreground"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCount)"
                    activeDot={{ r: 4, strokeWidth: 2, stroke: 'hsl(var(--chart-1))', fill: 'hsl(var(--background))' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Summary Statistics */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-muted-foreground">Average/day</p>
                  <p className="text-sm font-semibold">{avgReadings.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Peak day</p>
                  <p className="text-sm font-semibold">{maxReadings.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Data points</p>
                  <p className="text-sm font-semibold">{chartData.length}</p>
                </div>
              </div>
            </div>

            {/* Export button */}
            {!loading && chartData.length > 0 && (
              <div className="flex justify-end">
                <ChartExportButton
                  chartId="readings-trend-chart"
                  filename="readings-over-time"
                  metadata={{
                    title: 'IoT Device Readings Over Time',
                    description: 'Time series visualization of total readings collected by day',
                    dateRange: dateRange,
                    additionalInfo: {
                      'Total Readings': totalReadings.toLocaleString(),
                      'Average Daily Readings': avgReadings.toLocaleString(),
                      'Peak Reading Day': `${maxReadings.toLocaleString()} readings`,
                      'Data Points': chartData.length.toString(),
                      'Date Range': dateRange,
                    },
                  }}
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
